/**
 * The site administrator's two grants for one school.
 *
 * Injected into the school settings screen the same way AiControl is, rather
 * than rendered by that screen behind a role check - so the school-facing page
 * stays exactly the same for a school administrator, and the platform's
 * controls are visibly the platform's.
 *
 * Withdrawing a grant leaves the school's saved settings untouched. Every
 * resolve re-checks the grant, so revoking takes effect at once and restoring
 * gives the school back what it had - which is what makes this safe to use as
 * a suspension switch while a school fixes a broken relay.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { SchoolEmailSettings } from '../../lib/api/client'
import { Checkbox, Pill, Spinner } from '../../components/ui'
import { Icon } from '../../components/icons'
import { toast } from '../../components/toast'
import './email.css'

export function PlatformEmailControl({ schoolId }: { schoolId: string }) {
  const queryClient = useQueryClient()
  const key = ['platform', 'school-email', schoolId]
  const settings = useQuery({
    queryKey: key,
    queryFn: () => api.schoolEmailPermissions(schoolId),
  })

  const save = useMutation({
    mutationFn: (next: { allow_custom_smtp: boolean; allow_custom_templates: boolean }) =>
      api.setSchoolEmailPermissions(schoolId, next),
    onSuccess: (updated) => {
      queryClient.setQueryData(key, updated)
      // The school-facing panel on the same screen is driven by these grants.
      queryClient.invalidateQueries({ queryKey: ['school', 'email'] })
      toast('Email permissions saved.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  if (settings.isLoading) return <Spinner label="Loading email permissions" />
  if (!settings.data) return null

  const current: SchoolEmailSettings = settings.data
  const set = (field: 'allow_custom_smtp' | 'allow_custom_templates', value: boolean) =>
    save.mutate({
      allow_custom_smtp: current.allow_custom_smtp,
      allow_custom_templates: current.allow_custom_templates,
      [field]: value,
    })

  return (
    <section className="tc-email-panel" aria-labelledby="platform-email-control-title">
      <header className="tc-email-heading">
        <span className="tc-email-icon" aria-hidden="true">
          <Icon.schools size={20} />
        </span>
        <div>
          <span className="tc-email-eyebrow">Platform control</span>
          <h2 id="platform-email-control-title">Email permissions</h2>
          <p>
            What this school may run itself. With both off it sends through TeachCraftAI, using
            TeachCraftAI's wording.
          </p>
        </div>
        <Pill tone={current.use_custom_smtp ? 'success' : 'secondary'}>
          {current.active_source === 'school' ? 'Own server' : 'Platform server'}
        </Pill>
      </header>

      <div className="tc-email-body">
        {/* Side by side on a wide screen: two parallel choices, and stacking
            them left the right half of the card empty. */}
        <div className="tc-email-permissions">
          <div className="tc-email-permission">
            <Checkbox
              label="Allow their own mail server"
              checked={current.allow_custom_smtp}
              disabled={save.isPending}
              onChange={(e) => set('allow_custom_smtp', e.target.checked)}
            />
            <p className="tc-email-note">
              Lets the school send notifications from its own domain. They still have to switch it
              on and enter the server details themselves.
            </p>
          </div>

          <div className="tc-email-permission">
            <Checkbox
              label="Allow their own notification wording"
              checked={current.allow_custom_templates}
              disabled={save.isPending}
              onChange={(e) => set('allow_custom_templates', e.target.checked)}
            />
            <p className="tc-email-note">
              Lets the school reword each notification, and switch off any that TeachCraftAI has
              left on. A notification switched off here is off for them whatever they choose.
            </p>
          </div>
        </div>

        {(current.use_custom_smtp || current.use_custom_templates) && (
          <p className="tc-email-note">
            Turning a permission off takes effect immediately and does not delete anything the
            school has set up — switching it back on returns them to where they were.
          </p>
        )}
      </div>
    </section>
  )
}
